import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { CarePlan, Practitioner } from "@curai/js-fhir-tools";
import { formatDate } from "date-fns";
import { HeartPulse } from "lucide-react";
import React from "react";
import type { ChatMessage, ChatUserMap } from "../../types/chatMessageTypes";
import { MessageModelType } from "../../types/chatMessageTypes";
import { UserAvatar } from "../UserAvatar";

interface BulletPointProps {
  text: string;
}

const BulletPoint: React.FC<BulletPointProps> = ({ text }) => (
  <BodyText size="sm">{text}</BodyText>
);

interface CarePlanMessageProps {
  message: ChatMessage;
  userMap?: ChatUserMap;
}

const CarePlanBullets: React.FC<{ carePlan?: CarePlan }> = ({ carePlan }) => {
  // Ripping this over from the old EHR chat.
  const carePlanDescriptions = React.useMemo(() => {
    const descriptions: string[] = [];
    for (const activity of carePlan?.activity ?? []) {
      if (activity?.detail?.description) {
        descriptions.push(activity.detail.description);
      }
    }
    return descriptions;
  }, [carePlan]);

  const dispositionDescription = carePlan?.dispositionDescription || "";
  const followupTimingString = carePlan?.followupTimingText;
  const followUpText = `We'll follow up with you ${followupTimingString}. Please contact us sooner if you have any questions or concerns.`;

  const shouldShowDispositionDescription = Boolean(dispositionDescription);
  const shouldShowFollowupTiming = Boolean(followupTimingString);

  return (
    <ul className="list-disc list-outside pl-4">
      {carePlanDescriptions?.map((description) => (
        <li key={description}>
          <BulletPoint text={description} />
        </li>
      ))}
      {shouldShowDispositionDescription && (
        <li key="disposition-description-bullet">
          <BulletPoint text={dispositionDescription} />
        </li>
      )}
      {shouldShowFollowupTiming && (
        <li key="followup-bullet">
          <BulletPoint text={followUpText} />
        </li>
      )}
    </ul>
  );
};

export const CarePlanMessage = ({ message, userMap }: CarePlanMessageProps) => {
  const carePlan = message.carePlan
    ? new CarePlan(message.carePlan)
    : undefined;

  const user = message.userId
    ? userMap?.[message.userId.toString()]
    : undefined;
  const practitionerName = message.practitioner
    ? new Practitioner(message.practitioner).displayName
    : "Provider";

  const isAsync = message.modelType === MessageModelType.ASYNC_MESSAGE;

  return (
    <div className="flex flex-col gap-2">
      {/* This is a hack to not show double avatars for care plans on async messages */}
      {/* Once the message attribute work is done, we can remove this since our grouping logic will drastically simplify */}
      {!isAsync && <UserAvatar user={user} fallbackName={practitionerName} />}
      <div className="flex flex-col gap-1">
        <Card
          className={cn(
            "border w-full border-info",
            isAsync && "border-secondary",
          )}
          data-testid="care-plan-card"
        >
          <CardHeader>
            <div className="flex flex-col gap-1">
              <div className="flex items-start gap-1">
                <HeartPulse className="w-4 h-4" />
                <div className="flex flex-col">
                  <CardTitle>Your Care Plan For</CardTitle>
                  <Badge variant="info" className="w-fit">
                    <BodyText size="sm" textColor={"info"}>
                      {message.eocName}
                    </BodyText>
                  </Badge>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Separator />
            <CarePlanBullets carePlan={carePlan} />
          </CardContent>
        </Card>
        <BodyText size="xs" textColor={"tertiary"}>
          {formatDate(new Date(message.created), "MM/dd/yyyy h:mm a")}
        </BodyText>
      </div>
    </div>
  );
};
